import { useCallback } from 'react';

import { Pesel } from '@dreryk/personal-external-identifier-common-api';

import { ModalConfirmButton, ValidateFormEventInterceptor } from '@dreryk/react-components';

import { entitlementStatementsTranslationKeys } from '../../../translations';
import { useCreateRepresentativeEntitlementStatement } from '../../RepresentativeEntitlementStatement.hooks';
import { RepresentativeEntitlementStatementCreateFormController } from '../form/RepresentativeEntitlementStatementCreateForm';

import { DatesShowModalEventInterceptor } from './DatesShowModalEventInterceptor';

type RepresentativeModalConfirmationButtonProps = Pick<
   RepresentativeEntitlementStatementCreateFormController,
   'handleSubmit' | 'trigger'
> & {
   readonly patientId: number;
   readonly differentDates: boolean;
   readonly onDataChanged: VoidFunction;
   readonly setEntitlementStatementId: (id: number) => void;
};

export const RepresentativeModalConfirmationButton = ({
   handleSubmit,
   trigger,
   patientId,
   differentDates,
   onDataChanged,
   setEntitlementStatementId,
}: RepresentativeModalConfirmationButtonProps): JSX.Element => {
   const { executeAsync: createRepresentativeEntitlementStatement } = useCreateRepresentativeEntitlementStatement(
      patientId,
      onDataChanged
   );

   const submitRepresentative = useCallback(async (): Promise<void> => {
      await handleSubmit(async (formData) => {
         if (formData.existingRepresentative) {
            formData.firstName = formData.existingRepresentative.firstName.asString();
            formData.lastName = formData.existingRepresentative.lastName.asString();
            formData.guardianRelation = formData.existingRepresentative.relation;
            formData.pesel = formData.existingRepresentative.externalIdentifier.accept<Pesel | undefined>({
               visitPesel: (pesel) => pesel,
               visitPassport: () => undefined,
               visitNationalIdentificationNumber: () => undefined,
            });
         }
         const id = await createRepresentativeEntitlementStatement(formData);
         setEntitlementStatementId(id);
      })();
   }, [handleSubmit, createRepresentativeEntitlementStatement, setEntitlementStatementId]);

   return (
      <ValidateFormEventInterceptor trigger={trigger}>
         <DatesShowModalEventInterceptor differentDates={differentDates}>
            <ModalConfirmButton
               label={entitlementStatementsTranslationKeys.createModal.confirmButton}
               onClick={submitRepresentative}
            />
         </DatesShowModalEventInterceptor>
      </ValidateFormEventInterceptor>
   );
};
